// src/app/App.tsx
import React, { useEffect, useRef } from 'react';
import { RouterProvider } from 'react-router-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { Toaster } from 'react-hot-toast';
import { I18nextProvider } from 'react-i18next';
import i18n from '@/i18n/config';
import { router } from './router';
import { store, persistor } from './store';
import { Splash } from '@/features/shared/components/ui/Splash';
import { ErrorBoundary } from '../features/shared/components/ui/ErrorBoundary';
import { initializeAuthThunk } from '@/features/auth/state';
import { recheckSessionPrincipal } from '@/features/auth/state/principalSwitch';
import { useAppDispatch } from './hooks';
import { LocalizationProvider } from '@/features/localization/LocalizationProvider';
import { ThemeProvider } from '@/features/shared/theme/ThemeProvider';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';
import { ConfirmProvider } from '@/features/shared/components/confirm/ConfirmProvider';
import { RecoveryPrompt } from '@/features/shared/components/recovery/RecoveryPrompt';
import '@/styles/index.css';

// App initializer component - must be inside Provider to use hooks
const AppInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useAppDispatch();
  const hasInitialized = useRef(false);

  useEffect(() => {
    // Only initialize once
    if (hasInitialized.current) return;
    hasInitialized.current = true;
    
    // Initialize auth state
    dispatch(initializeAuthThunk());
    
    // Initialize app-wide configurations
    console.log('App initialized');
    
    // Set up global error handlers
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);
    };
    
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Re-validate roles when the tab regains focus, so a role change applies
    // without a manual reload (initializeAuthThunk compares token vs server roles
    // and logs out / syncs as needed). Throttled to once per 30s.
    let lastCheck = 0;
    const recheck = () => {
      if (document.visibilityState && document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastCheck < 30000) return;
      lastCheck = now;
      // If the shared session now belongs to someone else (a login in another
      // tab), reload so no screen keeps acting on the previous person's data.
      void recheckSessionPrincipal({
        currentUserId: () => store.getState().auth?.user?.id,
        revalidate: () => dispatch(initializeAuthThunk()),
        flush: () => persistor.flush(),
        reload: () => window.location.reload(),
      });
    };
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheck);
    };
  }, []); // Remove dispatch dependency to prevent re-runs

  return <>{children}</>;
};

export function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
      <I18nextProvider i18n={i18n}>
        <Provider store={store}>
          <PersistGate loading={<Splash />} persistor={persistor}>
            <QueryClientProvider client={queryClient}>
            <LocalizationProvider>
              <AppInitializer>
                <ConfirmProvider>
                <ImpersonationBanner />
                <RouterProvider
                  router={router}
                  future={{
                    v7_startTransition: true,
                  }}
                />
                <Toaster 
                  position="top-right"
                  toastOptions={{
                    duration: 4000,
                    style: {
                      background: '#363636',
                      color: '#fff',
                    },
                    success: {
                      style: {
                        background: '#10b981',
                      },
                    },
                    error: {
                      style: {
                        background: '#ef4444',
                      },
                    },
                  }}
                />
                {/* At the root, above the routes: it never replaces page
                    content, so an edited form keeps its state while this is
                    on screen. */}
                <RecoveryPrompt />
                </ConfirmProvider>
              </AppInitializer>
            </LocalizationProvider>
            </QueryClientProvider>
          </PersistGate>
        </Provider>
      </I18nextProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
